import { fabric } from 'fabric';
import type { MutableRefObject } from 'react';
import Vec2 from 'vec2';

import { CANVAS_CURSOR } from '~/core/constants';
import { DRAGGING_STATE } from '~/core/enums';
import { ApplianceMoveResult } from '~/core/hooks/useAppliance';
import { DrawingAppliance, DrawingShape } from '~/core/models';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Discriminated movement type for cleaner branching logic.
 */
export type MovementType =
  | 'canvas'
  | 'shape'
  | 'appliance-moved'
  | 'appliance-clicked';

/**
 * Parameters for determining movement type.
 */
export interface MovementContext {
  movingItem: DrawingShape | string | undefined;
  moveVec: Vec2;
}

/**
 * Parameters for resetting dragging state at end of mouse up.
 */
export interface DraggingResetParams {
  draggingStateRef: MutableRefObject<DRAGGING_STATE>;
  movingItemRef: MutableRefObject<DrawingShape | string | undefined>;
  canvas: fabric.Canvas;
}

/**
 * Parameters for animating appliance back to original position.
 */
export interface AnimateBackParams {
  fabricObj: fabric.Object;
  result: ApplianceMoveResult;
  canvas: fabric.Canvas;
}

/**
 * Parameters for updating appliance position after successful move.
 */
export interface UpdatePositionParams {
  fabricObj: fabric.Object;
  appliance: DrawingAppliance;
}

// ============================================================================
// MOVEMENT TYPE DETECTION
// ============================================================================

/**
 * Determines the type of movement being completed based on movingItemRef value.
 *
 * Movement types:
 * - 'canvas': No item being moved (canvas pan only)
 * - 'shape': movingItemRef is a DrawingShape object
 * - 'appliance-moved': movingItemRef is a string (appliance ID) with actual movement
 * - 'appliance-clicked': movingItemRef is a string but no movement occurred
 */
export const determineMovementType = ({
  movingItem,
  moveVec,
}: MovementContext): MovementType => {
  if (!movingItem) {
    return 'canvas';
  }

  if (typeof movingItem === 'object') {
    return 'shape';
  }

  // movingItem is a string (appliance ID)
  const hasMovement = moveVec.x !== 0 || moveVec.y !== 0;

  return hasMovement ? 'appliance-moved' : 'appliance-clicked';
};

// ============================================================================
// PREDICATES
// ============================================================================

/**
 * Checks if the appliance move failed and should animate again.
 */
export const shouldAnimateAgain = (
  fabricObj: fabric.Object | undefined,
  result: ApplianceMoveResult
): fabricObj is fabric.Object =>
  Boolean(fabricObj && result.appliance && !result.success);

/**
 * Checks if the appliance move succeeded and should update position.
 */
export const shouldUpdatePosition = (
  fabricObj: fabric.Object | undefined,
  result: ApplianceMoveResult
): fabricObj is fabric.Object =>
  Boolean(fabricObj && result.appliance && result.success);

// ============================================================================
// FABRIC OBJECT HELPERS
// ============================================================================

/**
 * Finds a fabric object by its name property.
 */
export const findFabricObjectByName = (
  canvas: fabric.Canvas,
  name: string
): fabric.Object | undefined =>
  canvas.getObjects().find((obj) => obj.name === name);

/**
 * Keeps an appliance selected after movement and triggers render.
 */
export const keepApplianceSelected = (
  canvas: fabric.Canvas,
  fabricObj: fabric.Object | undefined
): void => {
  if (fabricObj) {
    canvas.setActiveObject(fabricObj as fabric.Image);
    canvas.requestRenderAll();
  }
};

// ============================================================================
// STATE RESET
// ============================================================================

/**
 * Resets dragging state and cursor at the end of mouse up.
 * MUST be called at the end of EVERY mouse up, regardless of movement type.
 */
export const resetDraggingState = ({
  draggingStateRef,
  movingItemRef,
  canvas,
}: DraggingResetParams): void => {
  draggingStateRef.current = DRAGGING_STATE.IDLE;
  movingItemRef.current = undefined;
  canvas.defaultCursor = CANVAS_CURSOR.DEFAULT.HOVER;
};
